import type { ReactNode } from "react";
import {
  LabelLayoutProvider,
  type LabelAlign,
  type LabelPosition,
  type LabelWidth,
} from "./Internal/FormItemContext";

const labelWidthValues: Record<LabelWidth, string> = {
  sm: "100px",
  md: "150px",
  lg: "200px",
  xl: "260px",
};

type HeaderAlign = "left" | "right";

interface FormGroupProps {
  label?: string;
  description?: string;
  children: ReactNode;
  labelPosition?: LabelPosition;
  labelWidth?: LabelWidth;
  /** Alignment of the field labels within the label column. "beside" layout only. */
  labelAlign?: LabelAlign;
  /** Alignment of the group header label. Follows labelAlign unless set. */
  headerAlign?: HeaderAlign;
  /** Remove the vertical gap between rows (gap-y-0 instead of gap-y-3). */
  dense?: boolean;
}

export function FormGroup({
  label,
  description,
  children,
  labelPosition = "beside",
  labelWidth = "md",
  labelAlign = "right",
  headerAlign,
  dense,
}: FormGroupProps) {
  // A crisp semibold heading, one clear size step above the field labels
  // beneath it — so it reads as a section header whether those labels are the
  // muted "above" style or the crisp "beside" style.
  const sectionHeaderClass = "text-base font-semibold text-foreground";
  if (labelPosition === "beside") {
    const gapY = dense ? "gap-y-0" : "gap-y-3";
    return (
      <LabelLayoutProvider value={{ labelPosition, labelWidth, labelAlign }}>
        <div
          className={`grid items-center gap-x-4 ${gapY}`}
          style={{
            gridTemplateColumns: `${labelWidthValues[labelWidth]} minmax(0, 1fr)`,
          }}
        >
          {/* The header row spans the full group width so children still start
              on the next row. It follows labelAlign so the heading always sits
              directly above the field labels below it. When right, an inner
              block sized to the label column pins the text to that column's
              right edge (not floated out over the inputs); when left, both the
              heading and the labels share the group's left edge. */}
          {label && (
            <div className={`col-span-2 ${sectionHeaderClass}`}>
              {(headerAlign ?? labelAlign) === "right" ? (
                <div
                  className="text-right whitespace-nowrap"
                  style={{ width: labelWidthValues[labelWidth] }}
                >
                  {label}
                </div>
              ) : (
                label
              )}
            </div>
          )}
          {description && (
            <p className="col-span-2 text-sm text-content-secondary">
              {description}
            </p>
          )}
          {children}
        </div>
      </LabelLayoutProvider>
    );
  }

  return (
    <LabelLayoutProvider value={{ labelPosition, labelWidth, labelAlign }}>
      <div>
        {label && (
          <div
            className={`${sectionHeaderClass} ${description ? "mb-1" : "mb-3"}`}
          >
            {label}
          </div>
        )}
        {description && (
          <p className="mb-3 text-sm text-content-secondary">{description}</p>
        )}
        <div className="flex flex-col gap-3">{children}</div>
      </div>
    </LabelLayoutProvider>
  );
}
